import styled, { keyframes }from "styled-components";
import { ImageIF } from "../types";

/* ImageUploader.tsx는 이미지를 업로드하고 관리하는 컴포넌트 */

const BtnHoverAnimation = (before: string, after: string) => keyframes`
  0% {
    background-color: ${before};
  }

  100% {
    background-color: ${after};
  }
`;

interface ImgProps {
    images: ImageIF[];
    onImages: React.Dispatch<React.SetStateAction<ImageIF[]>>;
    onImageClick: (index: number) => void; // 이미지 클릭할 때 post에 삽입하는 callback func
    onDelClick: (index: number) => void; // 이미지 삭제할 때 post에서 제거하는 callback func
}

const Uploader = styled.input.attrs({type:'file', // 파일 get
                                    accept:'image/jpg, image/png, image/jpeg, image/gif' // 업로드 가능한 확장자 유형
                                    })`
    display: none;
`;

const UploadBtn = styled.label`
    display: flex;
    float: right;

    width: 1.5rem;
    height: 1.5rem;

    padding: auto;
    margin-left: 3rem;

    align-items: center;
    justify-content: center;

    font-size: 13px;
    border-radius: 0.5rem;
    border: 1px solid ${props => props.theme.BTN_ADD_DELETE.BORDER};
    color: ${props => props.theme.BTN_ADD_DELETE.FONT_COLOR};
    font-weight: 700;

    &:hover {
        animation: ${props => BtnHoverAnimation(props.theme.BTN_ADD_DELETE.HOVER.BACKGROUND.FIRST,
            props.theme.BTN_ADD_DELETE.HOVER.BACKGROUND.LAST)} 0.3s;
        background-color: ${props => props.theme.BTN_ADD_DELETE.HOVER.BACKGROUND.LAST};
        box-shadow: inset 0 6px 6px ${props => props.theme.BTN_ADD_DELETE.HOVER.SHADOW};
    }

    &:not(:hover) {
        animation: ${props => BtnHoverAnimation(props.theme.BTN_ADD_DELETE.HOVER.BACKGROUND.LAST,
            props.theme.BTN_ADD_DELETE.HOVER.BACKGROUND.FIRST)} 0.3s;
        background-color: ${props => props.theme.BTN_ADD_DELETE.HOVER.BACKGROUND.FIRST};
    }
`;

const Body = styled.div`
    overflow-y: auto;
    margin-top: 3rem;
`;

const ImgContainer = styled.div`
    position: relative; // z-index 기준점
    display: flex;

    width: 8rem;
    height: 6rem;

    margin-right: 1rem;

    border: 1px solid ${props => props.theme.IMG_CONTAINER.BORDER};
    border-radius: 6px;

    overflow: hidden;
    float: left;
`;

const ImgList = styled.div`
    margin: 0 auto;
`;

const Img = styled.img`
    width: 100%;
    height:100%;

    object-fit: contain;
    z-index:100;

    cursor: pointer; // 클릭 가능
    transition: filter 0.2s ease;

    &:hover {
        filter: ${props => props.theme.IMG_CONTAINER.BRIGHTNESS};
    }
`;

const DeleteBtn = styled.button`
    display: flex;
    position: absolute;

    left: 1px;
    top: 1px;

    width: 1rem;
    height: 1rem;
    color: ${props => props.theme.BTN_ADD_DELETE.FONT_COLOR};

    font-size: 10px;
    align-items: center;
    justify-content: center;

    border: 1px solid ${props => props.theme.BTN_ADD_DELETE.BORDER};
    border-radius: 50%;
    z-index: 150;

    &:hover {
        animation: ${props => BtnHoverAnimation(props.theme.BTN_ADD_DELETE.HOVER.BACKGROUND.FIRST,
            props.theme.BTN_ADD_DELETE.HOVER.BACKGROUND.LAST)} 0.3s;
        background-color: ${props => props.theme.BTN_ADD_DELETE.HOVER.BACKGROUND.LAST};
        box-shadow: inset 0 6px 6px ${props => props.theme.BTN_ADD_DELETE.HOVER.SHADOW};
    }

    &:not(:hover) {
        animation: ${props => BtnHoverAnimation(props.theme.BTN_ADD_DELETE.HOVER.BACKGROUND.LAST,
            props.theme.BTN_ADD_DELETE.HOVER.BACKGROUND.FIRST)} 0.3s;
        background-color: ${props => props.theme.BTN_ADD_DELETE.HOVER.BACKGROUND.FIRST};
    }
`;

// 업로드 전 이미지를 canvas로 리사이즈/재압축해 용량을 줄이는 헬퍼
async function compressImage(file: File, maxSize = 1600, quality = 0.7): Promise<File> {
    if (file.type === 'image/gif') return file; // gif는 애니메이션 깨지니 원본 유지

    const dataUrl = await new Promise<string>((res, rej) => {
        const reader = new FileReader();
        reader.onload = () => res(reader.result as string);
        reader.onerror = rej;
        reader.readAsDataURL(file);
    });

    const img = await new Promise<HTMLImageElement>((res, rej) => {
        const i = new Image();
        i.onload = () => res(i);
        i.onerror = rej;
        i.src = dataUrl;
    });

    let w = img.width;
    let h = img.height;
    if (Math.max(w, h) > maxSize) {
        const r = maxSize / Math.max(w, h);
        w = Math.round(w * r);
        h = Math.round(h * r);
    }

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    canvas.getContext('2d')!.drawImage(img, 0, 0, w, h);

    const blob = await new Promise<Blob>((res, rej) =>
        canvas.toBlob(b => b ? res(b) : rej(new Error('toBlob failed')), 'image/jpeg', quality)
    );

    if (blob.size >= file.size) return file; // 압축본이 더 크면 원본 사용

    return new File([blob], file.name.replace(/\.\w+$/, '.jpg'), { type: 'image/jpeg' });
}

function ImgUploader({images, onImages, onImageClick, onDelClick}:ImgProps){
    const addImages = async (event:React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;

        // null일 수도 있으므로
        if (!files) return;

        const newImages = await Promise.all(
            Array.from(files).map(async (file) => {
                const compressed = await compressImage(file);
                return { file: compressed, preview: URL.createObjectURL(compressed) };
            })
        );

        onImages(prev => {
            const merged = [...prev, ...newImages];
            if (merged.length > 20) {
                alert("콘텐츠당 최대 20개 이미지만 업로드 할 수 있습니다.");
                return merged.slice(0, 20); // 20개 이상이라면 20개까지 자르기
            }
            return merged;
        });
    }

    const deleteImages = (id:number) => {
        onImages(images.filter((_, idx) => idx !== id));
    }

    return (<>
        <Uploader id="imageUpload" multiple onChange={addImages}/>
        <UploadBtn htmlFor="imageUpload">+</UploadBtn>
        <Body>
            <ImgList>
                {images.map((imgObj, id) => (
                <ImgContainer key={id}>
                    <Img src={
                        imgObj.preview.startsWith('blob:')
                        ? imgObj.preview
                        : `${process.env.REACT_APP_API_URL}${imgObj.preview}`
                    } alt={`${imgObj.preview}-${id}`}
                        onClick={() => {console.log(imgObj.preview);
                        onImageClick(id)}}/>
                    <DeleteBtn onClick={() => {
                        onDelClick(id);
                        deleteImages(id);
                    }}>X</DeleteBtn>
                </ImgContainer>
                ))}
            </ImgList>
        </Body>
    </>
    );
}

export default ImgUploader;
